import { Fragment, useState } from 'react';
import styled from '@emotion/styled';
import { Table } from 'semantic-ui-react';
import { ExpandButton } from '@britishcouncil/react-admin';
import { DayOfTheWeekBadge } from 'components';
import { useNavigationContext } from 'hooks';
import { testCentreOverviewStore } from 'store';
import { TestCentreOverviewDateDto } from 'api/planning';
import { TestCentreOverviewQueryFilters } from 'store/test-centre-overview/service';
import { COLUMN_ORDER, getDateCellContent } from './table-columns';
import { TestCentreOverviewDistrictRows } from './TestCentreOverviewDistrictRows';
import { LoadingRow } from './LoadingRow';

export const TestCentreOverviewTable = ({
  filters,
}: {
  filters: TestCentreOverviewQueryFilters;
}) => {
  const { centreId } = useNavigationContext();

  const { data: dates, isFetching } =
    testCentreOverviewStore.service.useGetTestCentreOverviewDatesQuery(
      { centreId, filters },
      { skip: !centreId }
    );
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const toggleDate = (date: string) =>
    setExpandedDates((prev) => {
      const next = new Set(prev);

      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }

      return next;
    });

  const rows = dates ?? [];

  return (
    <S.TableWrapper>
      <Table celled compact striped size="small">
        <TestCentreOverviewTableHeader />
        <Table.Body>
          {isFetching && <LoadingRow colSpan={COLUMN_ORDER.length} label="Loading..." />}
          {!isFetching && rows.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={COLUMN_ORDER.length} textAlign="center">
                No planned sessions found
              </Table.Cell>
            </Table.Row>
          )}
          {!isFetching &&
            centreId &&
            rows.map((date) => (
              <TestCentreOverviewDateRow
                key={date.date}
                centreId={centreId}
                date={date}
                filters={filters}
                isOpen={expandedDates.has(date.date)}
                onToggle={() => toggleDate(date.date)}
              />
            ))}
        </Table.Body>
      </Table>
    </S.TableWrapper>
  );
};

const TestCentreOverviewDateRow = ({
  centreId,
  date,
  filters,
  isOpen,
  onToggle,
}: {
  centreId: number;
  date: TestCentreOverviewDateDto;
  filters: TestCentreOverviewQueryFilters;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <Fragment>
    <Table.Row>
      {COLUMN_ORDER.map((key) => (
        <Table.Cell key={key}>
          {key === 'date' ? (
            <S.DateCell>
              <ExpandButton
                open={isOpen}
                onClick={onToggle}
                size="mini"
                secondary={false}
                color="blue"
                style={{ margin: 0 }}
              />
              <DayOfTheWeekBadge date={date.date} size="mini" color="blue" />
              {date.date}
            </S.DateCell>
          ) : (
            getDateCellContent(key, date)
          )}
        </Table.Cell>
      ))}
    </Table.Row>
    {isOpen && (
      <TestCentreOverviewDistrictRows centreId={centreId} date={date.date} filters={filters} />
    )}
  </Fragment>
);

const TestCentreOverviewTableHeader = () => (
  <Table.Header>
    <Table.Row>
      <Table.HeaderCell rowSpan={3} textAlign="center">
        Date / Location
      </Table.HeaderCell>
      <Table.HeaderCell colSpan={6} textAlign="center">
        Speaking
      </Table.HeaderCell>
      <Table.HeaderCell colSpan={7} textAlign="center">
        LRW
      </Table.HeaderCell>
      <Table.HeaderCell colSpan={2} textAlign="center" className="wrap">
        Registration status confirmed?
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={3} textAlign="center">
        Utilisation
      </Table.HeaderCell>
    </Table.Row>
    <Table.Row>
      <Table.HeaderCell
        rowSpan={2}
        textAlign="center"
        style={{ borderLeft: '1px solid rgba(34,36,38,.1)' }}
      >
        Venue
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center" className="wrap">
        Planned slots
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center" className="wrap">
        Booked slots
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        OSR
      </Table.HeaderCell>
      <Table.HeaderCell colSpan={2} textAlign="center">
        In-person examiners assigned?
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        Venue
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        Session time
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center" className="wrap">
        Planned seats
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center" className="wrap">
        Booked seats
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        AC
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        GT
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        OSR
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        Yes
      </Table.HeaderCell>
      <Table.HeaderCell rowSpan={2} textAlign="center">
        No
      </Table.HeaderCell>
    </Table.Row>
    <Table.Row>
      <Table.HeaderCell textAlign="center" style={{ borderLeft: '1px solid rgba(34,36,38,.1)' }}>
        Yes
      </Table.HeaderCell>
      <Table.HeaderCell textAlign="center">No</Table.HeaderCell>
    </Table.Row>
  </Table.Header>
);

const HEADER_ROW_HEIGHT = '2.5rem';
const HEADER_ROW_1_HEIGHT = '3.25rem';

const S = {
  TableWrapper: styled.div`
    thead th {
      position: sticky;
      z-index: 1;
      white-space: nowrap;
    }

    thead th.wrap {
      white-space: normal;
    }

    thead tr:nth-of-type(1) th {
      top: 0;
      height: ${HEADER_ROW_1_HEIGHT};
    }

    thead tr:nth-of-type(2) th {
      top: ${HEADER_ROW_1_HEIGHT};
      height: ${HEADER_ROW_HEIGHT};
    }

    thead tr:nth-of-type(3) th {
      top: calc(${HEADER_ROW_1_HEIGHT} + ${HEADER_ROW_HEIGHT});
      height: ${HEADER_ROW_HEIGHT};
    }

    .ui.table {
      border-top: 0;
      border-bottom: 0;
    }

    .ui.table th,
    .ui.table td {
      padding-left: 0.35rem;
      padding-right: 0.35rem;
    }
  `,
  DateCell: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
  `,
};
